import { useState, useCallback, useEffect } from 'react';
import type { LogisticsObjectScope } from '@/types/logisticsObject';
import {
  getSavedQueries,
  saveQuery,
  deleteSavedQuery,
  setSavedQueryDefault,
  getDefaultSavedQuery,
} from '@/utils/savedQueriesStorage';
import { SavedQuery } from '@/types/savedQuery';
import { useSnackbarNotification } from './useSnackbarNotification';

/**
 * Custom hook for managing saved queries
 * Follows DRY, KISS, and YAGNI principles
 */
export const useSavedQueries = (logisticsObjectType: LogisticsObjectScope) => {
  const { showSuccess, showError } = useSnackbarNotification();
  
  // Direct state management - simpler and more explicit
  const [savedQueries, setSavedQueries] = useState<SavedQuery[]>(() => 
    getSavedQueries(logisticsObjectType)
  );

  // Update queries when logistics object type changes
  useEffect(() => {
    setSavedQueries(getSavedQueries(logisticsObjectType));
  }, [logisticsObjectType]);

  // Save a new query or replace existing one with same name
  const handleSaveQuery = useCallback(
    (queryName: string, query: unknown, isDefault: boolean = false) => {
      try {
        const newQuery = isDefault
          ? saveQuery(queryName, query, logisticsObjectType, true)
          : saveQuery(queryName, query, logisticsObjectType);
        
        // Check if we're replacing an existing query
        setSavedQueries(prev => {
          let updatedQueries: SavedQuery[];

          const existingIndex = prev.findIndex(q => q.name === queryName);
          if (existingIndex >= 0) {
            // Replace existing query
            const updated = [...prev];
            updated[existingIndex] = newQuery;
            updatedQueries = updated;
          } else {
            // Add new query at the beginning
            updatedQueries = [newQuery, ...prev];
          }

          if (newQuery.isDefault) {
            return updatedQueries.map((savedQuery) => ({
              ...savedQuery,
              isDefault: savedQuery.id === newQuery.id,
            }));
          }

          return updatedQueries;
        });
        
        showSuccess(`Query "${queryName}" saved successfully`);
        return true;
      } catch (error) {
        console.error('Error saving query:', error);
        showError('Failed to save query');
        return false;
      }
    },
    [logisticsObjectType, showSuccess, showError]
  );

  const handleSetDefaultQuery = useCallback(
    (queryId: string, isDefault: boolean) => {
      try {
        const updatedQueries = setSavedQueryDefault(queryId, logisticsObjectType, isDefault);
        setSavedQueries(updatedQueries);
        showSuccess(isDefault ? 'Default query updated successfully' : 'Default query removed successfully');
        return true;
      } catch (error) {
        console.error('Error updating default query:', error);
        showError('Failed to update default query');
        return false;
      }
    },
    [logisticsObjectType, showSuccess, showError],
  );

  // Delete a saved query
  const handleDeleteQuery = useCallback(
    (queryId: string) => {
      try {
        deleteSavedQuery(queryId, logisticsObjectType);
        setSavedQueries(prev => prev.filter(q => q.id !== queryId));
        showSuccess('Query deleted successfully');
        return true;
      } catch (error) {
        console.error('Error deleting query:', error);
        showError('Failed to delete query');
        return false;
      }
    },
    [logisticsObjectType, showSuccess, showError]
  );

  return {
    savedQueries,
    saveQuery: handleSaveQuery,
    deleteQuery: handleDeleteQuery,
    setDefaultQuery: handleSetDefaultQuery,
    defaultQuery: getDefaultSavedQuery(logisticsObjectType),
  };
};
